"use client";

import { c as _c } from "react/compiler-runtime";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { type LLMOutputComponent, useLLMOutput } from "@llm-ui/react";
import { markdownLookBack } from "@llm-ui/markdown";

// CRITICAL FIX: Small version for history with text-xs styling
const SmallMarkdownComponent: LLMOutputComponent = t0 => {
  const $ = _c(5);
  const {
    blockMatch
  } = t0;
  const markdown = blockMatch.output;
  let t1;
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [remarkGfm];
    t2 = [rehypeRaw];
    t3 = {
      p: _temp,
      ul: _temp2,
      ol: _temp3,
      li: _temp4,
      code: _temp5,
      pre: _temp6,
      blockquote: _temp7,
      strong: _temp8,
      em: _temp9,
      a: _temp10,
      table: _temp11,
      thead: _temp12,
      tbody: _temp13,
      tr: _temp14,
      th: _temp15,
      td: _temp16,
      iframe: _temp17,
      video: _temp19
    };
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
  } else {
    t1 = $[0];
    t2 = $[1];
    t3 = $[2];
  }
  let t4;
  if ($[3] !== markdown) {
    t4 = <ReactMarkdown remarkPlugins={t1} rehypePlugins={t2} components={t3}>{markdown}</ReactMarkdown>;
    $[3] = markdown;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  return t4;
};

// Create font-size specific markdown components
const createMarkdownComponent = (fontSize: 'small' | 'medium' | 'large' | 'extra-large'): LLMOutputComponent => {
  const Component: LLMOutputComponent = t0 => {
    const $ = _c(20);
    const {
      blockMatch
    } = t0;
    const textClass = fontSize === "small" ? "text-sm" : fontSize === "large" ? "text-lg" : fontSize === "extra-large" ? "text-xl" : "text-base";
    const h1Class = fontSize === "small" ? "text-lg" : fontSize === "large" ? "text-3xl" : fontSize === "extra-large" ? "text-4xl" : "text-2xl";
    const h2Class = fontSize === "small" ? "text-base" : fontSize === "large" ? "text-2xl" : fontSize === "extra-large" ? "text-3xl" : "text-xl";
    const h3Class = fontSize === "small" ? "text-sm" : fontSize === "large" ? "text-xl" : "text-lg";
    const h4Class = fontSize === "small" ? "text-sm" : fontSize === "large" ? "text-lg" : "text-base";
    const h5Class = fontSize === "small" ? "text-xs" : fontSize === "large" ? "text-base" : "text-sm";
    const h6Class = fontSize === "small" ? "text-xs" : fontSize === "large" ? "text-sm" : "text-xs";
    const thClass = fontSize === "small" ? "text-xs" : fontSize === "large" ? "text-sm" : "text-xs";
    const markdown = blockMatch.output;
    let t1;
    let t10;
    let t11;
    let t12;
    let t13;
    let t14;
    let t15;
    let t2;
    let t3;
    let t4;
    let t5;
    let t6;
    let t7;
    let t8;
    let t9;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = [remarkGfm];
      t2 = [rehypeRaw];
      t3 = t16 => {
        const {
          children
        } = t16;
        return <h1 className={`font-bold mb-4 mt-6 first:mt-0 ${h1Class}`}>{children}</h1>;
      };
      t4 = t17 => {
        const {
          children: children_0
        } = t17;
        return <h2 className={`font-semibold mb-3 mt-5 first:mt-0 ${h2Class}`}>{children_0}</h2>;
      };
      t5 = t18 => {
        const {
          children: children_1
        } = t18;
        return <h3 className={`font-semibold mb-3 mt-4 first:mt-0 ${h3Class}`}>{children_1}</h3>;
      };
      t6 = t19 => {
        const {
          children: children_2
        } = t19;
        return <h4 className={`font-medium mb-2 mt-3 first:mt-0 ${h4Class}`}>{children_2}</h4>;
      };
      t7 = t20 => {
        const {
          children: children_3
        } = t20;
        return <h5 className={`font-medium mb-2 mt-3 first:mt-0 ${h5Class}`}>{children_3}</h5>;
      };
      t8 = t21 => {
        const {
          children: children_4
        } = t21;
        return <h6 className={`font-medium mb-2 mt-3 first:mt-0 ${h6Class}`}>{children_4}</h6>;
      };
      t9 = t22 => {
        const {
          children: children_5
        } = t22;
        return <p className={`mb-3 last:mb-0 leading-relaxed ${textClass}`}>{children_5}</p>;
      };
      t10 = t23 => {
        const {
          children: children_6,
          ...props
        } = t23;
        return <ul style={{
          display: "block",
          listStyleType: "disc",
          paddingInlineStart: "40px",
          marginBottom: "0.75rem"
        }} className={textClass} {...props}>{children_6}</ul>;
      };
      t11 = t24 => {
        const {
          children: children_7,
          ...props_0
        } = t24;
        return <ol style={{
          display: "block",
          listStyleType: "decimal",
          paddingInlineStart: "40px",
          marginBottom: "0.75rem"
        }} className={textClass} {...props_0}>{children_7}</ol>;
      };
      t12 = t25 => {
        const {
          children: children_8
        } = t25;
        return <li className={`leading-relaxed ${textClass}`}>{children_8}</li>;
      };
      t13 = t26 => {
        const {
          children: children_9,
          className
        } = t26;
        const isInline = !className;
        if (isInline) {
          return <code className={`bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded font-mono ${textClass}`}>{children_9}</code>;
        }
        return <code className={`${className} ${textClass}`}>{children_9}</code>;
      };
      t14 = t27 => {
        const {
          children: children_10
        } = t27;
        return <div className={`whitespace-pre-wrap wrap-break-word my-3 ${textClass}`}>{children_10}</div>;
      };
      t15 = t28 => {
        const {
          children: children_11
        } = t28;
        return <blockquote className={`border-l-4 border-gray-300 pl-4 italic mb-3 text-gray-700 dark:text-gray-300 ${textClass}`}>{children_11}</blockquote>;
      };
      $[0] = t1;
      $[1] = t10;
      $[2] = t11;
      $[3] = t12;
      $[4] = t13;
      $[5] = t14;
      $[6] = t15;
      $[7] = t2;
      $[8] = t3;
      $[9] = t4;
      $[10] = t5;
      $[11] = t6;
      $[12] = t7;
      $[13] = t8;
      $[14] = t9;
    } else {
      t1 = $[0];
      t10 = $[1];
      t11 = $[2];
      t12 = $[3];
      t13 = $[4];
      t14 = $[5];
      t15 = $[6];
      t2 = $[7];
      t3 = $[8];
      t4 = $[9];
      t5 = $[10];
      t6 = $[11];
      t7 = $[12];
      t8 = $[13];
      t9 = $[14];
    }
    let t16;
    let t17;
    if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
      t16 = t18 => {
        const {
          children: children_19
        } = t18;
        return <th className={`px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200 last:border-r-0 ${thClass}`}>{children_19}</th>;
      };
      t17 = t19 => {
        const {
          children: children_20
        } = t19;
        const processContent = content => {
          if (typeof content === "string") {
            const parts = content.split(/<br\s*\/?>/gi);
            if (parts.length > 1) {
              return parts.map((part, index) => <span key={index}>{part}{index < parts.length - 1 && <br />}</span>);
            }
            return content;
          }
          if (Array.isArray(content)) {
            return content.map((item, index_0) => <span key={index_0}>{processContent(item)}</span>);
          }
          return content;
        };
        return <td className={`px-3 py-2 text-gray-900 border-r border-gray-200 last:border-r-0 whitespace-pre-wrap ${textClass}`}>{processContent(children_20)}</td>;
      };
      $[15] = t16;
      $[16] = t17;
    } else {
      t16 = $[15];
      t17 = $[16];
    }
    let t18;
    if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
      t18 = {
        h1: t3,
        h2: t4,
        h3: t5,
        h4: t6,
        h5: t7,
        h6: t8,
        p: t9,
        ul: t10,
        ol: t11,
        li: t12,
        code: t13,
        pre: t14,
        blockquote: t15,
        strong: _temp20,
        em: _temp21,
        a: _temp22,
        table: _temp23,
        thead: _temp24,
        tbody: _temp25,
        tr: _temp26,
        th: t16,
        td: t17,
        iframe: _temp27,
        video: t19 => {
          const {
            src: src_0,
            children: children_21,
            ...props_2
          } = t19;
          if (!src_0) {
            return <div className="border border-yellow-200 bg-yellow-50 p-4 rounded-lg text-sm text-yellow-800 my-4"><p className="font-medium">⚠️ Video source missing</p><p className="text-xs mt-1">Please provide a valid video URL</p></div>;
          }
          const getVideoType = _temp28;
          return <div className="relative w-full my-4"><video controls={true} preload="metadata" className="w-full h-auto rounded-lg" style={{
              maxHeight: "70vh"
            }} {...props_2}><source src={src_0} type={getVideoType(src_0)} />{children_21}<p className={`text-gray-600 ${textClass}`}>Your browser does not support the video tag.<a href={src_0} className="text-brand-primary hover:underline ml-1" target="_blank" rel="noopener noreferrer">Download video</a></p></video></div>;
        }
      };
      $[17] = t18;
    } else {
      t18 = $[17];
    }
    let t19;
    if ($[18] !== markdown) {
      t19 = <ReactMarkdown remarkPlugins={t1} rehypePlugins={t2} components={t18}>{markdown}</ReactMarkdown>;
      $[18] = markdown;
      $[19] = t19;
    } else {
      t19 = $[19];
    }
    return t19;
  };
  Component.displayName = `MarkdownComponent(${fontSize})`;
  return Component;
};
const MediumMarkdownComponent = createMarkdownComponent('medium');
const SmallMarkdownComponentCustom = createMarkdownComponent('small');
const LargeMarkdownComponent = createMarkdownComponent('large');
const ExtraLargeMarkdownComponent = createMarkdownComponent('extra-large');
// Legacy MarkdownComponent - redirects to medium size for backward compatibility
const MarkdownComponent: LLMOutputComponent = t0 => {
  const $ = _c(2);
  const {
    blockMatch
  } = t0;
  let t1;
  if ($[0] !== blockMatch) {
    t1 = <MediumMarkdownComponent blockMatch={blockMatch} />;
    $[0] = blockMatch;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};

// Shared component that uses the useLLMOutput hook
export const StreamingLLMOutput = t0 => {
  const $ = _c(12);
  const {
    content,
    isStreamFinished,
    fontSize: t1
  } = t0;
  const fontSize = t1 === undefined ? "medium" : t1;
  const Component = fontSize === "small" ? SmallMarkdownComponentCustom : fontSize === "large" ? LargeMarkdownComponent : fontSize === "extra-large" ? ExtraLargeMarkdownComponent : MediumMarkdownComponent;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = markdownLookBack();
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== Component) {
    t3 = {
      component: Component,
      lookBack: t2
    };
    $[1] = Component;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [];
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== content || $[5] !== isStreamFinished || $[6] !== t3) {
    t5 = {
      llmOutput: content,
      fallbackBlock: t3,
      blocks: t4,
      isStreamFinished
    };
    $[4] = content;
    $[5] = isStreamFinished;
    $[6] = t3;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const {
    blockMatches
  } = useLLMOutput(t5);
  let t6;
  if ($[8] !== blockMatches) {
    t6 = blockMatches.map(_temp29);
    $[8] = blockMatches;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t6) {
    t7 = <div className="llm-output">{t6}</div>;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  return t7;
};

// CRITICAL FIX: Small version for history with consistent text-xs styling
export const StreamingLLMOutputSmall = t0 => {
  const $ = _c(9);
  const {
    content,
    isStreamFinished
  } = t0;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      component: SmallMarkdownComponent,
      lookBack: markdownLookBack()
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  let t3;
  if ($[2] !== content || $[3] !== isStreamFinished) {
    t3 = {
      llmOutput: content,
      fallbackBlock: t1,
      blocks: t2,
      isStreamFinished
    };
    $[2] = content;
    $[3] = isStreamFinished;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const {
    blockMatches
  } = useLLMOutput(t3);
  let t4;
  if ($[5] !== blockMatches) {
    t4 = blockMatches.map(_temp30);
    $[5] = blockMatches;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t4) {
    t5 = <div className="llm-output">{t4}</div>;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  return t5;
};
function _temp(t0) {
  const {
    children
  } = t0;
  return <p className="mb-2 last:mb-0 leading-relaxed text-xs">{children}</p>;
}
function _temp2(t0) {
  const {
    children: children_0,
    ...props
  } = t0;
  return <ul style={{
    display: "block",
    listStyleType: "disc",
    paddingInlineStart: "20px",
    marginBottom: "0.5rem"
  }} {...props}>{children_0}</ul>;
}
function _temp3(t0) {
  const {
    children: children_1,
    ...props_0
  } = t0;
  return <ol style={{
    display: "block",
    listStyleType: "decimal",
    paddingInlineStart: "20px",
    marginBottom: "0.5rem"
  }} {...props_0}>{children_1}</ol>;
}
function _temp4(t0) {
  const {
    children: children_2
  } = t0;
  return <li className="leading-relaxed text-xs">{children_2}</li>;
}
function _temp5(t0) {
  const {
    children: children_3,
    className
  } = t0;
  const isInline = !className;
  if (isInline) {
    return <code className="bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded text-xs font-mono">{children_3}</code>;
  }
  return <code className={className}>{children_3}</code>;
}
function _temp6(t0) {
  const {
    children: children_4
  } = t0;
  return <pre className="bg-gray-100 dark:bg-gray-800 p-2 rounded-lg overflow-x-auto mb-2 text-xs">{children_4}</pre>;
}
function _temp7(t0) {
  const {
    children: children_5
  } = t0;
  return <blockquote className="border-l-2 border-gray-300 pl-2 italic mb-2 text-gray-700 dark:text-gray-300 text-xs">{children_5}</blockquote>;
}
function _temp8(t0) {
  const {
    children: children_6
  } = t0;
  return <strong className="font-semibold text-xs">{children_6}</strong>;
}
function _temp9(t0) {
  const {
    children: children_7
  } = t0;
  return <em className="italic text-xs">{children_7}</em>;
}
function _temp10(t0) {
  const {
    children: children_8,
    href
  } = t0;
  return <a href={href} className="text-[#6252fd] hover:underline text-xs" target="_blank" rel="noopener noreferrer">{children_8}</a>;
}
function _temp11(t0) {
  const {
    children: children_9
  } = t0;
  return <div className="overflow-x-auto mb-2"><table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded text-xs">{children_9}</table></div>;
}
function _temp12(t0) {
  const {
    children: children_10
  } = t0;
  return <thead className="bg-gray-50">{children_10}</thead>;
}
function _temp13(t0) {
  const {
    children: children_11
  } = t0;
  return <tbody className="bg-white divide-y divide-gray-200">{children_11}</tbody>;
}
function _temp14(t0) {
  const {
    children: children_12
  } = t0;
  return <tr className="hover:bg-gray-50">{children_12}</tr>;
}
function _temp15(t0) {
  const {
    children: children_13
  } = t0;
  return <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200 last:border-r-0">{children_13}</th>;
}
function _temp16(t0) {
  const {
    children: children_14
  } = t0;
  const processContent = content => {
    if (typeof content === "string") {
      const parts = content.split(/<br\s*\/?>/gi);
      if (parts.length > 1) {
        return parts.map((part, index) => <span key={index}>{part}{index < parts.length - 1 && <br />}</span>);
      }
      return content;
    }
    if (Array.isArray(content)) {
      return content.map((item, index_0) => <span key={index_0}>{processContent(item)}</span>);
    }
    return content;
  };
  return <td className="px-2 py-1 text-xs text-gray-900 border-r border-gray-200 last:border-r-0 whitespace-pre-wrap">{processContent(children_14)}</td>;
}
function _temp17(t0) {
  const {
    src,
    title,
    ...props_1
  } = t0;
  const allowedDomains = ["demo.arcade.software", "app.arcade.software"];
  const isAllowed = allowedDomains.some(domain => src?.includes(domain));
  if (!src || !isAllowed) {
    return <div className="border border-red-200 bg-red-50 p-4 rounded-lg text-sm text-red-800 my-4"><p className="font-medium">⚠️ Iframe blocked for security</p><p className="text-xs mt-1">Only Arcade.software embeds are allowed. URL: {src || "missing"}</p></div>;
  }
  const embedUrl = src.includes("?embed") ? src : `${src}?embed`;
  return <div className="relative w-full my-4" style={{
    paddingBottom: "56.25%"
  }}><iframe src={embedUrl} title={title || "Arcade Demo"} loading="lazy" allowFullScreen={true} allow="fullscreen" className="absolute top-0 left-0 w-full h-full rounded-lg border-0" style={{
      maxWidth: "100%",
      maxHeight: "70vh"
    }} {...props_1} /></div>;
}
function _temp18(url) {
  if (url.endsWith(".webm")) {
    return "video/webm";
  }
  if (url.endsWith(".ogg")) {
    return "video/ogg";
  }
  if (url.endsWith(".mov")) {
    return "video/quicktime";
  }
  return "video/mp4";
}
function _temp19(t0) {
  const {
    src: src_0,
    children: children_15,
    ...props_2
  } = t0;
  if (!src_0) {
    return <div className="border border-yellow-200 bg-yellow-50 p-4 rounded-lg text-sm text-yellow-800 my-4"><p className="font-medium">⚠️ Video source missing</p><p className="text-xs mt-1">Please provide a valid video URL</p></div>;
  }
  const getVideoType = _temp18;
  return <div className="relative w-full my-4"><video controls={true} preload="metadata" className="w-full h-auto rounded-lg" style={{
      maxHeight: "70vh"
    }} {...props_2}><source src={src_0} type={getVideoType(src_0)} />{children_15}<p className="text-gray-600 text-xs">Your browser does not support the video tag.<a href={src_0} className="text-brand-primary hover:underline ml-1" target="_blank" rel="noopener noreferrer">Download video</a></p></video></div>;
}
function _temp20(t0) {
  const {
    children: children_12
  } = t0;
  return <strong className="font-semibold">{children_12}</strong>;
}
function _temp21(t0) {
  const {
    children: children_13
  } = t0;
  return <em className="italic">{children_13}</em>;
}
function _temp22(t0) {
  const {
    children: children_14,
    href
  } = t0;
  return <a href={href} className="text-[#6252fd] hover:underline" target="_blank" rel="noopener noreferrer">{children_14}</a>;
}
function _temp23(t0) {
  const {
    children: children_15
  } = t0;
  return <div className="overflow-x-auto mb-4"><table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-lg">{children_15}</table></div>;
}
function _temp24(t0) {
  const {
    children: children_16
  } = t0;
  return <thead className="bg-gray-50">{children_16}</thead>;
}
function _temp25(t0) {
  const {
    children: children_17
  } = t0;
  return <tbody className="bg-white divide-y divide-gray-200">{children_17}</tbody>;
}
function _temp26(t0) {
  const {
    children: children_18
  } = t0;
  return <tr className="hover:bg-gray-50">{children_18}</tr>;
}
function _temp27(t0) {
  const {
    src,
    title,
    ...props_1
  } = t0;
  const allowedDomains = ["demo.arcade.software", "app.arcade.software"];
  const isAllowed = allowedDomains.some(domain => src?.includes(domain));
  if (!src || !isAllowed) {
    return <div className="border border-red-200 bg-red-50 p-4 rounded-lg text-sm text-red-800 my-4"><p className="font-medium">⚠️ Iframe blocked for security</p><p className="text-xs mt-1">Only Arcade.software embeds are allowed. URL: {src || "missing"}</p></div>;
  }
  const embedUrl = src.includes("?embed") ? src : `${src}?embed`;
  return <div className="relative w-full my-4" style={{
    paddingBottom: "56.25%"
  }}><iframe src={embedUrl} title={title || "Arcade Demo"} loading="lazy" allowFullScreen={true} allow="fullscreen" className="absolute top-0 left-0 w-full h-full rounded-lg border-0" style={{
      maxWidth: "100%",
      maxHeight: "70vh"
    }} {...props_1} /></div>;
}
function _temp28(url) {
  if (url.endsWith(".webm")) {
    return "video/webm";
  }
  if (url.endsWith(".ogg")) {
    return "video/ogg";
  }
  if (url.endsWith(".mov")) {
    return "video/quicktime";
  }
  return "video/mp4";
}
function _temp29(blockMatch, index) {
  const BlockComponent = blockMatch.block.component;
  return <BlockComponent key={index} blockMatch={blockMatch} />;
}
function _temp30(blockMatch, index) {
  const Component = blockMatch.block.component;
  return <Component key={index} blockMatch={blockMatch} />;
}